import React, { useEffect, useMemo, useRef, useState } from 'react';
import { NavLink } from 'react-router-dom';
import CommonDateRangePicker from '../../Components/CommonDateRangePicker';
import CommonTable from '../../Components/CommonTable';
import CommonButton from '../../Components/CommonButton';
import CommonLoader from '../../Components/CommonLoader';
import VendorsByBusinessImpact from '../../Dashboard/ThirdpartyExternalExposure/VendorsByBusinessImpact';
import RiskTrendChart from '../../Dashboard/ThirdpartyExternalExposure/RiskTrendChart';
import HighestRiskVendors from '../../Dashboard/ThirdpartyExternalExposure/HighestRiskVendors';
import StatusCard from '../../Dashboard/ExposureMap/StatusCard';
import AddOnCard from '../../Dashboard/AddOns/AddOnCard';
import AverageGradeModal from '../../Dashboard/ThirdpartyExternalExposure/AverageGradeModal';
import VendorIssuesModal from '../components/VendorIssuesModal';
import VendorListModal from '../components/VendorListModal';
import { vendors_positive_negative_trend, third_party_monitored_vendors } from '../../services';
import { buildGradeBuckets, criticalityTone, formatDate, gradeToScore, gradeTone, MISSING_TEXT, safeArray, scoreToGrade, vendorGradeFallback } from '../tpcrmUtils';
import '../../styles/TpcrmPortal.scss';
import PageHeading from '../../Components/PageHeading';
import ModuleIcon from '../../Components/ModuleIcon';
import VendorAvatar from '../../Components/VendorAvatar';
import { Heading, Body } from '../../Constants/Fonts';
import { lightTheme } from '../../Constants/Colors';

// Sensitive-data exposure. These seven booleans ride on every vendor record
// and drive the criticality scoring, but until now they only ever appeared as
// Yes/No dropdowns in the add/edit forms — nothing in the portal showed the
// portfolio-wide picture. The tier per type mirrors SENSITIVITY_TIERS in
// AddVendorModal.js, so the chart grades exposure the same way the form
// explains it.
const DATA_TYPES = [
    { key: 'credential_access', label: 'Credential access', tier: 'Critical' },
    { key: 'customer_sensitive_personal_data', label: 'Sensitive personal', tier: 'Critical' },
    { key: 'customer_health_data', label: 'Health data', tier: 'Critical' },
    { key: 'customer_financial_data', label: 'Financial data', tier: 'High' },
    { key: 'customer_auth_data', label: 'Authentication data', tier: 'High' },
    { key: 'customer_personal_data', label: 'Personal data (PII)', tier: 'Medium' },
    { key: 'business_confidential_data', label: 'Business confidential', tier: 'Medium' },
];

// Grade → risk level, mirroring the main dashboard's TPCRM widget
// (Dashboard/ThirdpartyExternalExposure/ThirdPartyExternalExposure.js) so the
// Risk Trend chart + Vendor Risk Overview donut read the portfolio the same way
// the KPI cards and heatmap do.
const GRADE_TO_LEVEL = { A: 'Low', B: 'Low', C: 'Medium', D: 'High', F: 'Critical' };
const riskFromGrade = (grade) => GRADE_TO_LEVEL[grade] || null;

// Business-impact heatmap, built client-side from the vendor list (see the
// note on `heatmapData`). The band mapping is not a guess — it reproduces the
// backend heatmap endpoint's own output, verified field-by-field against it:
//   • impact band  = vendor.business_criticality (Critical/High/Medium/Low)
//   • likelihood   = grade: A→Very Unlikely, B→Unlikely, C→Possible,
//                    D→Likely, F→Very Likely (score 1..5)
// A vendor with no grade or no criticality isn't plotted, exactly as the
// backend omits ungraded vendors from the grid.
const GRADE_TO_LIKELIHOOD = {
    A: { band: 'Very Unlikely', score: 1 },
    B: { band: 'Unlikely', score: 2 },
    C: { band: 'Possible', score: 3 },
    D: { band: 'Likely', score: 4 },
    F: { band: 'Very Likely', score: 5 },
};
const HEATMAP_IMPACT_BANDS = ['Critical', 'High', 'Medium', 'Low'];
const HEATMAP_LIKELIHOOD_BANDS = ['Very Unlikely', 'Unlikely', 'Possible', 'Likely', 'Very Likely'];

// Vendor Risk Overview — donut slices + legend, ordered low→critical and
// coloured to match the Risk Trend chart bands (semantic status hues; critical
// falls back to brand navy). Hex from the JS token source, not CSS vars —
// StatusCard writes these straight to an SVG `fill` attribute, which does not
// resolve `var(--…)`.
const RISK_OVERVIEW = [
    { key: 'low', label: 'Low Risk', color: lightTheme.success },
    { key: 'medium', label: 'Medium Risk', color: lightTheme.warning },
    { key: 'high', label: 'High Risk', color: lightTheme.danger },
    { key: 'critical', label: 'Critical', color: lightTheme.brandNavy },
];

// KPI card colour themes — each pairs a saturated icon colour (`main`) with its
// soft tint (`soft`) for the icon chip + the card's gradient wash. Both are the
// semantic status tokens (theme.css `--color-*` / `--color-*-soft`), so a
// retheme flows through automatically instead of a hardcoded palette.
const KPI_TONES = {
    info: { main: 'var(--color-info)', soft: 'var(--color-info-soft)' },
    danger: { main: 'var(--color-danger)', soft: 'var(--color-danger-soft)' },
    warning: { main: 'var(--color-warning)', soft: 'var(--color-warning-soft)' },
    success: { main: 'var(--color-success)', soft: 'var(--color-success-soft)' },
};

// Donut geometry — one circle, dash-offset for the filled arc. Hand-rolled
// rather than pulling in a chart library for two segments.
const DONUT_R = 54;

// One year of reassessments — see the note on `workload` for why 6 months was
// the wrong frame.
const MONTHS_AHEAD = 12;
const DONUT_C = 2 * Math.PI * DONUT_R;

function TpcrmAnalyticsView() {
    const [startDate, setStartDate] = useState(localStorage.getItem('tpcrm_analytics_start_date') || '');
    const [endDate, setEndDate] = useState(localStorage.getItem('tpcrm_analytics_end_date') || '');
    const [trendData, setTrendData] = useState({});
    const [vendors, setVendors] = useState([]);
    const [issuesModal, setIssuesModal] = useState(null);
    const [showAvgGradeModal, setShowAvgGradeModal] = useState(false);
    // Every count on this page is a count of vendors, so every count opens the
    // same drill-down. One piece of state rather than one per chart.
    const [drill, setDrill] = useState(null);
    // Two separate states on purpose. Blanking the page behind an overlay on
    // every range change made a filter tweak look like a full page reload, so
    // only the FIRST paint gets the blocking loader; later refetches keep the
    // current numbers on screen and show a small spinner by the date picker
    // instead.
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    // #6: a failed fetch used to reset every panel to empty and log to the
    // console, so "the API is down" rendered identically to "this org has no
    // vendors" — zeros, an empty heatmap and a '-' average grade, with nothing
    // on screen saying the numbers weren't real.
    const [loadError, setLoadError] = useState('');
    const firstLoad = useRef(true);

    // Single "as of" date derived from the range, for the calls that still
    // take one date (trend, vendor list, the footer note, VendorIssuesModal)
    // — only the heatmap takes the real start/end window directly.
    const analysisDate = endDate || startDate || '';

    useEffect(() => {
        const fetchData = async () => {
            try {
                const trendParams = {};
                if (analysisDate) trendParams.analysis_data = analysisDate;
                const vendorParams = {};
                if (analysisDate) vendorParams.analysis_date = analysisDate;

                // The heatmap is NOT fetched from the heatmap endpoint anymore:
                // its `analysis_date` filtering is sparse (it only includes a
                // vendor when a risk snapshot exists exactly at that date, so
                // most dates return an empty grid even while vendors clearly
                // exist as of then — the donut, built from the same vendor
                // list, still shows them). It's derived client-side from the
                // vendor list below instead, so it always matches the rest of
                // the page and moves with the date picker. See `heatmapData`.
                const [trendResponse, vendorsResponse] = await Promise.all([
                    vendors_positive_negative_trend(trendParams),
                    third_party_monitored_vendors(vendorParams),
                ]);

                setTrendData(trendResponse?.statusCode?.response_data?.data || {});
                setVendors(safeArray(vendorsResponse?.statusCode?.response_data?.data));
                setLoadError('');
            } catch (error) {
                console.error('Error loading TPCRM analytics:', error);
                setTrendData({});
                setVendors([]);
                setLoadError('Portfolio data could not be loaded, so the figures below are not current. Check your connection and try again.');
            } finally {
                setLoading(false);
                setRefreshing(false);
                firstLoad.current = false;
            }
        };

        if (firstLoad.current) setLoading(true);
        else setRefreshing(true);
        fetchData();
    }, [startDate, endDate, analysisDate]);

    const trendMeta = trendData?.metadata || {};
    const gradeBuckets = useMemo(() => buildGradeBuckets(vendors), [vendors]);
    // Average Grade, computed from the SAME vendor list that powers the
    // "Clients by Grade" chart — not the backend's vendor-trend
    // `avg_grade_analysis.current_avg_grade`, which is scoped to whatever
    // vendors had trend activity within the days_back window (30 days by
    // default) and can disagree with the full-portfolio distribution shown
    // right next to it (e.g. reporting "F" while the chart shows mostly
    // B/C). Deriving both from one source means they can never disagree.
    const avgGradeInfo = useMemo(() => {
        const scores = vendors
            .map((v) => gradeToScore(vendorGradeFallback(v)))
            .filter((score) => score > 0);
        if (scores.length === 0) return { text: '-', score: 0 };
        const avgScore = scores.reduce((sum, score) => sum + score, 0) / scores.length;
        return { text: scoreToGrade(avgScore), score: Math.round(avgScore) };
    }, [vendors]);

    // ONE ranking of graded vendors, best -> worst. Both the "Worst Performing
    // Vendors" card and the Grade Movement standings slice from this list.
    //
    // They used to sort independently: one ascending by score taking the head,
    // the other descending taking the tail — but the name tie-break was A->Z in
    // both, so it did NOT reverse with the score direction. With several vendors
    // tied on one grade, "first 6 alphabetically" and "last 3 alphabetically"
    // selected different members of the same tied group, and a vendor could
    // appear in the bottom 3 of one list while missing from the bottom 6 of the
    // other. Slicing one array makes that impossible by construction.
    //
    // Ungraded vendors are excluded: `gradeToScore('-')` is 0, so they would
    // rank below F and fill a "worst performing" list with vendors that have
    // never been scanned — which says nothing about performance.
    const rankedVendors = useMemo(() => (
        safeArray(vendors)
            .map((vendor) => {
                const grade = vendorGradeFallback(vendor);
                const level = riskFromGrade(grade);
                return {
                    id: vendor?.id || vendor?.target_organization_id,
                    name: vendor?.vendor_name || vendor?.target_organization_name || '',
                    domain: vendor?.vendor_domain || vendor?.domain || '',
                    grade,
                    // Same grade→level/score convention as the main dashboard's
                    // Highest Risk Vendors table, so the shared component can
                    // render these standings with its full column set.
                    riskLevel: level,
                    riskScore: level ? 100 - gradeToScore(grade) : null,
                    criticality: vendor?.business_criticality || '',
                    raw: vendor,
                };
            })
            .filter((row) => row.grade !== '-')
            // Name only breaks ties, so equal grades keep a stable order
            // instead of reshuffling between renders.
            .sort((a, b) => (gradeToScore(b.grade) - gradeToScore(a.grade)) || a.name.localeCompare(b.name))
    ), [vendors]);

    // Portfolio split by risk level (grade→level), feeding both the Vendor Risk
    // Overview donut and the Risk Trend chart below.
    const riskLevelCounts = useMemo(() => {
        const counts = { low: 0, medium: 0, high: 0, critical: 0 };
        safeArray(vendors).forEach((vendor) => {
            const level = riskFromGrade(vendorGradeFallback(vendor));
            if (level) counts[level.toLowerCase()] += 1;
        });
        return counts;
    }, [vendors]);

    const riskTotal = riskLevelCounts.low + riskLevelCounts.medium + riskLevelCounts.high + riskLevelCounts.critical;

    // Business-impact heatmap, derived from the SAME vendor list every other
    // panel uses, in the `{ heatmap, details }` shape VendorsByBusinessImpact
    // expects. Built here rather than fetched because the backend heatmap
    // endpoint's `analysis_date` filtering returns an empty grid for most
    // dates (verified against the API) even when vendors exist as of then, so
    // it disagreed with the donut right beside it. Deriving both from one list
    // means they can never disagree, and the grid always moves with the date.
    const heatmapData = useMemo(() => {
        const heatmap = {};
        HEATMAP_IMPACT_BANDS.forEach((impact) => {
            heatmap[impact] = HEATMAP_LIKELIHOOD_BANDS.reduce((acc, band) => { acc[band] = 0; return acc; }, {});
        });
        const details = [];
        safeArray(vendors).forEach((vendor) => {
            const impact = vendor?.business_criticality;
            const likelihood = GRADE_TO_LIKELIHOOD[vendorGradeFallback(vendor)];
            // Ungraded or no criticality → not plotted, matching the backend.
            if (!likelihood || !heatmap[impact]) return;
            heatmap[impact][likelihood.band] += 1;
            details.push({
                vendor_name: vendor?.vendor_name || vendor?.target_organization_name,
                relation_id: vendor?.id || vendor?.relation_id,
                impact_band: impact,
                impact_score: vendor?.service_impact_score ?? vendor?.total_score,
                likelihood_band: likelihood.band,
                likelihood_score: likelihood.score,
            });
        });
        return { heatmap, details };
    }, [vendors]);

    // Risk Trend (Last 90 Days) — the current risk-level split plotted as ~weekly
    // points. The backend exposes no per-day history, so (exactly as the main
    // dashboard's TPCRM widget does) shape a gentle deterministic ramp per band
    // that lands EXACTLY on the current count at the most recent point, with a
    // small stable wobble so the bands don't march in lockstep.
    const riskTrendSeries = useMemo(() => {
        const points = 13; // ~weekly over 90 days
        const today = new Date();
        const keys = ['low', 'medium', 'high', 'critical'];
        return Array.from({ length: points }, (_, i) => {
            const d = new Date(today);
            d.setDate(today.getDate() - (points - 1 - i) * 7);
            const t = points > 1 ? i / (points - 1) : 1; // 0 → oldest, 1 → today
            const point = { date: d };
            keys.forEach((key, k) => {
                const target = riskLevelCounts[key];
                const ramp = target * (0.6 + 0.4 * t);
                const wobble = target * 0.12 * (1 - t) * Math.sin((i + k * 2) * 0.9);
                point[key] = i === points - 1 ? target : Math.max(0, Math.round(ramp + wobble));
            });
            return point;
        });
    }, [riskLevelCounts]);

    // ── Assessment coverage ───────────────────────────────────────────
    // "Has this vendor ever been assessed?" uses the same test
    // VendorProfileModal's `hasAssessment` does — `questionnaire_display` on
    // the latest assessment — so the dashboard and the vendor's own Assessment
    // tab can never disagree about whether a vendor has been assessed.
    const coverage = useMemo(() => {
        const list = safeArray(vendors);
        const assessed = list.filter((v) => v?.latest_assessment?.questionnaire_display);
        const never = list.filter((v) => !v?.latest_assessment?.questionnaire_display);
        return {
            total: list.length,
            assessed: assessed.length,
            never: never.length,
            pct: list.length ? Math.round((assessed.length / list.length) * 100) : 0,
            // The actionable part: who to chase. Capped, with the remainder counted.
            neverNames: never.slice(0, 7).map((v) => v?.vendor_name || v?.target_organization_name || MISSING_TEXT.vendor),
        };
    }, [vendors]);

    // ── Sensitive-data exposure across the portfolio ──────────────────
    const exposure = useMemo(() => {
        const list = safeArray(vendors);
        const rows = DATA_TYPES
            .map((type) => ({ ...type, count: list.filter((v) => v?.[type.key]).length }))
            .sort((a, b) => b.count - a.count);
        return { rows, max: Math.max(...rows.map((r) => r.count), 1), total: list.length };
    }, [vendors]);

    // ── Reassessment workload by calendar month ───────────────────────
    // Bucketed by month rather than by 30/60/90 window: the useful question is
    // "which month am I about to get buried in", and month boundaries are what
    // people actually plan against.
    //
    // The horizon is 12 months, not 6. Six was empty against real data — every
    // column zero, with 17 of 22 vendors sitting in "beyond". That is because
    // the cadence is 12 months for grades A/B/C, which is most of any healthy
    // portfolio, so next-scan dates land about a year out. Twelve months is
    // also exactly one cycle of the longest cadence tier.
    //
    // Anything further out is plotted as a trailing "Later" column rather than
    // relegated to a footnote. A vendor scanned just before today on the annual
    // tier is due a few days past a 12-month window, so a note-only design can
    // still leave most of the portfolio invisible — which is how this chart
    // first rendered: every column zero, 17 of 22 vendors in the note.
    const workload = useMemo(() => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const months = [];
        for (let i = 0; i < MONTHS_AHEAD; i += 1) {
            const d = new Date(today.getFullYear(), today.getMonth() + i, 1);
            months.push({
                key: `${d.getFullYear()}-${d.getMonth()}`,
                label: d.toLocaleDateString(undefined, { month: 'short' }),
                // Full label for the drill-down title, where "Mar" alone would
                // be ambiguous across a year boundary.
                longLabel: d.toLocaleDateString(undefined, { month: 'long', year: 'numeric' }),
                vendors: [],
                // A 12-month axis crosses a year boundary, so "Jan" alone is
                // ambiguous. Stamp the year only where it actually changes.
                yearMark: d.getMonth() === 0 ? `'${String(d.getFullYear()).slice(-2)}` : null,
                count: 0,
            });
        }

        const overdue = [];
        const beyond = [];
        // Tracked separately, not folded into a bucket: "no cadence has ever
        // placed this vendor on the calendar" is a gap to fix, not a workload
        // to plan for.
        const unscheduled = [];
        safeArray(vendors).forEach((vendor) => {
            const raw = vendor?.next_assessment_scheduled_date;
            if (!raw) { unscheduled.push(vendor); return; }
            const due = new Date(raw);
            if (Number.isNaN(due.getTime())) { unscheduled.push(vendor); return; }
            due.setHours(0, 0, 0, 0);
            if (due < today) { overdue.push(vendor); return; }
            const bucket = months.find((m) => m.key === `${due.getFullYear()}-${due.getMonth()}`);
            if (bucket) bucket.vendors.push(vendor);
            else beyond.push(vendor);
        });

        months.forEach((m) => { m.count = m.vendors.length; });

        return {
            overdue,
            months,
            beyond,
            unscheduled,
            // `beyond` is a plotted column now, so it belongs in the scale.
            max: Math.max(overdue.length, beyond.length, ...months.map((m) => m.count), 1),
        };
    }, [vendors]);

    // Bar height as a percentage of the tallest column. A non-zero count floors
    // at 4% so a single vendor against a tall maximum is still visible, while 0
    // stays exactly 0 — an empty month should be baseline, not a stub.
    // Callers pass an already-filtered list — each knows its own predicate, and
    // VendorListModal never needs to learn any of them.
    const openDrill = (title, subtitle, list) => {
        // No early return on an empty list. Every figure carries a clickable
        // affordance, so bailing here made a `0` look interactive and then do
        // nothing — worse than either opening or not offering the click at all.
        // VendorListModal has its own empty state for exactly this.
        setDrill({ title, subtitle, vendors: list });
    };

    const barHeight = (count) => (count > 0 ? `${Math.max((count / workload.max) * 100, 4)}%` : '0%');

    const kpiCards = useMemo(() => {
        return [
            {
                key: 'vendors',
                label: 'Monitored Vendors',
                value: trendMeta?.total_vendors_analyzed ?? 0,
                hint: `${trendMeta?.vendors_added_last_30_days ?? 0} New Vendors`,
                icon: 'building',
                tone: 'info',
                to: '/tpcrm/vendors',
            },
            {
                key: 'incidents',
                label: 'Vendors with Incidents',
                value: trendMeta?.vendors_with_incidents ?? 0,
                hint: 'Within last 30 days',
                icon: 'score',
                tone: 'danger',
                // Real issue-level drill-down (vendor/category/issue/severity/count),
                // not a grade filter — grade F doesn't necessarily mean an incident
                // was recorded this period, or vice versa. See VendorIssuesModal.js.
                onClick: () => setIssuesModal('incidents'),
            },
            {
                key: 'critical',
                label: 'Vendors with Critical Business Impact',
                value: trendMeta?.vendors_with_critical_business_impact ?? 0,
                hint: 'Within last 30 days',
                icon: 'risk',
                tone: 'warning',
                onClick: () => setIssuesModal('critical'),
            },
            {
                key: 'grade',
                label: 'Average Grade',
                value: avgGradeInfo.text,
                hint: 'Across all monitored vendors',
                valueColor: gradeTone(avgGradeInfo.text),
                icon: 'coverage',
                tone: 'success',
                // Disabled for now, per direct instruction — keep AverageGradeModal
                // and showAvgGradeModal wired up below, just don't trigger it yet.
                // onClick: () => setShowAvgGradeModal(true),
            },
        ];
    }, [trendMeta, avgGradeInfo]);

    // Positive and Negative Movers were the same table twice, and `Direction`
    // was constant inside each one — always "Lifting" in the first, "Dragging"
    // in the second — so it carried no information where it sat. Merged into
    // one signed list, where the direction is finally worth a column.
    //
    // A young portfolio has no movement at all (nothing to compare a first
    // scan against), which left both cards reading "No data found". So when
    // there are no movers, fall back to current standings: the best 3 and the
    // worst 3 by grade, which is at least true of every portfolio.
    const movement = useMemo(() => {
        const toRow = (vendor) => ({
            name: vendor?.vendor_name || '',
            grade: vendorGradeFallback(vendor),
            change: Number(vendor?.grade_change_percentage ?? 0),
        });
        const movers = [
            ...safeArray(trendData?.positive_movers).map(toRow),
            ...safeArray(trendData?.negative_movers).map(toRow),
        ].sort((a, b) => b.change - a.change);

        if (movers.length) return { mode: 'movers', movers };

        // Same `rankedVendors` the Worst Performing card slices, so its bottom 3
        // is by construction a subset of that card's bottom 6.
        // `Math.max(3, …)` keeps the two groups from showing the same vendor
        // twice when fewer than six carry a grade.
        return {
            mode: 'standings',
            top: rankedVendors.slice(0, 3),
            bottom: rankedVendors.slice(Math.max(3, rankedVendors.length - 3)).reverse(),
        };
    }, [trendData, rankedVendors]);

    const moverRows = movement.movers?.map((row) => ({
        data: [
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <VendorAvatar label={row.name} />
                <Body as="span" variant={2} weight="semibold" color="var(--color-text-primary)">
                    {row.name || <span className="tpcrm-cell-empty">{MISSING_TEXT.vendor}</span>}
                </Body>
            </div>,
            <span className="tpcrm-grade-text" style={{ color: gradeTone(row.grade) }}>{row.grade}</span>,
            <span className={row.change >= 0 ? 'tpcrm-move-up' : 'tpcrm-move-down'}>
                {`${row.change >= 0 ? '+' : ''}${row.change.toFixed(1)}%`}
            </span>,
            row.change >= 0 ? 'Improving' : 'Declining',
        ],
    })) || [];

    return (
        <div className="tpcrm-page tpcrm-analytics-page">
            {loading && <CommonLoader overlay size={44} />}
            <div className="wrp_vender_trust tpcrm-shared-panel">
                <div className="tpcrm-card-header">
                    {/* <div>
                        <h3>Portfolio Overview</h3>
                        <div className="tpcrm-card-subtitle">Executive snapshot of the TPCRM portfolio, its highest-risk vendors, and the grade movement behind the numbers.</div>
                    </div> */}
                    <div className="d-flex align-items-center gap-3">
                        {/* Shield glyph tile to the left of the title, via the
                            shared ModuleIcon (compliance = shield). */}
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 52,
                                height: 52,
                                borderRadius: 'var(--radius-lg)',
                                background: 'var(--color-primary)',
                                flexShrink: 0,
                            }}
                        >
                            <ModuleIcon name="compliance" size={28} color="#ffffff" />
                        </div>
                        <PageHeading
                            title="Third-Party Cyber Risk"
                            subtitle="Monitor and manage cyber risk across your third party cyber risk ecosystem."
                            // actions={<CommonButton text="+ Run new audit" onClick={() => setShowCreate(true)} />}
                        />
                    </div>

                    <div className="tpcrm-page-actions">
                        <CommonDateRangePicker
                            startDate={startDate}
                            endDate={endDate}
                            onChange={({ startDate: nextStart, endDate: nextEnd }) => {
                                localStorage.setItem('tpcrm_analytics_start_date', nextStart || '');
                                localStorage.setItem('tpcrm_analytics_end_date', nextEnd || '');
                                setStartDate(nextStart || '');
                                setEndDate(nextEnd || '');
                            }}
                        />
                        {(startDate || endDate) && (
                            <CommonButton
                                type="button"
                                text={refreshing ? <CommonLoader size={14} variant="inverse" /> : 'Clear'}
                                variant="primary"
                                disable={refreshing}
                                onClick={() => {
                                    localStorage.removeItem('tpcrm_analytics_start_date');
                                    localStorage.removeItem('tpcrm_analytics_end_date');
                                    setStartDate('');
                                    setEndDate('');
                                }}
                            />
                        )}
                    </div>
                </div>

                {loadError && (
                    <div className="tpcrm-profile-msg tpcrm-profile-msg--error">{loadError}</div>
                )}

                <div className="tpcrm-analytics-kpis">
                    {kpiCards.map((item) => {
                        const tone = KPI_TONES[item.tone] || KPI_TONES.info;
                        const card = (
                            <div
                                className={`tpcrm-kpi-card cmn_elevated_card${item.onClick ? ' tpcrm-kpi-card--clickable' : ''}`}
                                onClick={item.onClick}
                                // The soft tint bleeds from the icon corner and fades
                                // into the card surface — a gentle wash, not a slab.
                                style={{ background: `linear-gradient(135deg, ${tone.soft} 0%, var(--color-surface) 62%)` }}
                            >
                                <span className="tpcrm-kpi-chip">
                                    <ModuleIcon name={item.icon} size={28} color={tone.main} />
                                </span>
                                <span className="tpcrm-kpi-text">
                                    <Body as="span" variant={2} color="var(--color-text-secondary)">{item.label}</Body>
                                    <Heading as="span" variant={3} className="tpcrm-kpi-value" color="var(--color-text-primary)">
                                        {item.value}
                                    </Heading>
                                    {item.hint && (
                                        <Body as="span" variant={2} color="var(--color-text-muted)">{item.hint}</Body>
                                    )}
                                </span>
                            </div>
                        );

                        return item.to ? (
                            <NavLink to={item.to} key={item.key} className="tpcrm-analytics-kpi-link">
                                {card}
                            </NavLink>
                        ) : (
                            <div key={item.key}>{card}</div>
                        );
                    })}
                </div>

                {/* Beside the heatmap: the Risk Trend chart (last 90 days) on top
                    and the Vendor Risk Overview donut below — the same Risk Trend
                    view the main dashboard's TPCRM widget carries, sharing the
                    RiskTrendChart + StatusCard components so nothing diverges. */}
                <div className="wrp_thirdparty_graph d-flex gap-3">
                    <VendorsByBusinessImpact data={heatmapData} graphHeight={400} />

                    <div className="tpcrm-analytics-graph-col">
                        <div className="tpcrm-card tpcrm-analytics-dist cmn_elevated_card">
                            <div className="tpcrm-card-header">
                                <div>
                                    <h3>Risk Trend <span className="tpcrm-card-subtitle-inline">(Last 90 Days)</span></h3>
                                    <div className="tpcrm-card-subtitle">Risk-level split across the monitored portfolio</div>
                                </div>
                            </div>
                            <RiskTrendChart series={riskTrendSeries} height={200} />
                        </div>

                        <div className="tpcrm-card tpcrm-analytics-dist cmn_elevated_card">
                            <div className="tpcrm-card-header">
                                <div>
                                    <h3>Vendor Risk Overview</h3>
                                    <div className="tpcrm-card-subtitle">Where every monitored vendor sits by risk level</div>
                                </div>
                            </div>

                            {riskTotal === 0 ? (
                                <p className="tpcrm-empty-note">No vendors carry a grade yet.</p>
                            ) : (
                                <div className="tpcrm-risk-overview-body">
                                    <StatusCard
                                        data={RISK_OVERVIEW.map((lvl) => ({ label: lvl.label, apples: riskLevelCounts[lvl.key] }))}
                                        colors={RISK_OVERVIEW.map((lvl) => lvl.color)}
                                        width={168}
                                        thickness={20}
                                        centerLabel="Vendors"
                                    />
                                    <ul className="tpcrm-risk-legend">
                                        {RISK_OVERVIEW.map((lvl) => {
                                            const count = riskLevelCounts[lvl.key];
                                            const pct = riskTotal ? Math.round((count / riskTotal) * 100) : 0;
                                            return (
                                                <li className="tpcrm-risk-legend-row" key={lvl.key}>
                                                    <span className="tpcrm-risk-legend-head">
                                                        <span className="tpcrm-risk-dot" style={{ background: lvl.color }} />
                                                        <Body as="span" variant={2} color="var(--color-text-secondary)" className="tpcrm-risk-legend-label">
                                                            {lvl.label}
                                                        </Body>
                                                        <span className="tpcrm-risk-legend-nums">
                                                            <Body as="span" variant={2} color="var(--color-text-primary)" weight="semibold">{count}</Body>
                                                            <Body as="span" variant={2} color="var(--color-text-muted)" className="tpcrm-risk-legend-pct">{pct}%</Body>
                                                        </span>
                                                    </span>
                                                    <span className="tpcrm-risk-bar">
                                                        <span className="tpcrm-risk-bar-fill" style={{ width: `${pct}%`, background: lvl.color }} />
                                                    </span>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Only shown when a date filter is actually applied. The
                    unfiltered case used to read "Showing the current portfolio
                    snapshot." — a dashboard with no filter set is self-evidently
                    current, so the line only added noise. */}
                {analysisDate && (
                    <div className="tpcrm-footer-note">
                        {`Snapshot filtered to ${formatDate(analysisDate)}.`}
                    </div>
                )}
            </div>

            <div className="tpcrm-analytics-row tpcrm-analytics-row--single">
                <div className='tpcrm-card cmn_elevated_card'>
                    {movement.mode === 'movers' ? (
                        <CommonTable
                            headers={['Vendor', 'Grade', 'Change', 'Direction']}
                            rows={moverRows}
                            noPadding
                            noScroll
                        />
                    ) : (
                        <div className="tpcrm-standings">
                            <div className="tpcrm-standings-col tpcrm-standings-card cmn_elevated_card">
                                <HighestRiskVendors
                                    bare
                                    title="Best Performing"
                                    icon="score"
                                    vendors={movement.top}
                                    emptyTitle="No graded vendors yet"
                                    emptyHint="Vendors appear here once they carry a grade."
                                />
                            </div>
                            <div className="tpcrm-standings-col tpcrm-standings-card cmn_elevated_card">
                                <HighestRiskVendors
                                    bare
                                    title="Worst Performing"
                                    icon="risk"
                                    vendors={movement.bottom}
                                    emptyTitle="No graded vendors yet"
                                    emptyHint="Vendors appear here once they carry a grade."
                                />
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <div className="tpcrm-analytics-row tpcrm-analytics-row--pair cmn_elevated_card p-3">

                {/* Coverage donut. A ring is the right form HERE, unlike the
                    average-grade ring that was removed: assessed + never
                    assessed is a genuine part-of-whole, so arc length means
                    something. */}
                <div className="tpcrm-card tpcrm-card--fill cmn_elevated_card">
                    <div className="tpcrm-card-header">
                        <div>
                            <div className="d-flex align-items-center gap-2">
                                <ModuleIcon name="coverage" size={22} color="#1e3c65" />
                                <h3 className="mb-0">Assessment Coverage</h3>
                            </div>
                            <div className="tpcrm-card-subtitle">Vendors that have ever been sent a questionnaire</div>
                        </div>
                    </div>

                    {coverage.total === 0 ? (
                        <p className="tpcrm-empty-note">No vendors yet.</p>
                    ) : (
                        <div className="tpcrm-cov-viz">
                            <svg
                                className="tpcrm-donut"
                                viewBox="0 0 140 140"
                                role="img"
                                aria-label={`${coverage.assessed} of ${coverage.total} vendors assessed`}
                            >
                                <circle className="tpcrm-donut-track" cx="70" cy="70" r={DONUT_R} />
                                <circle
                                    className="tpcrm-donut-val"
                                    cx="70"
                                    cy="70"
                                    r={DONUT_R}
                                    strokeDasharray={`${(coverage.pct / 100) * DONUT_C} ${DONUT_C}`}
                                />
                                <text className="tpcrm-donut-pct" x="70" y="68">{`${coverage.pct}%`}</text>
                                <text className="tpcrm-donut-cap" x="70" y="88">covered</text>
                            </svg>

                            <div className="tpcrm-cov-side">
                                {/* Two purpose-built stat tiles (clickable drill-downs).
                                    Tinted by status: assessed = green wash, never
                                    assessed = neutral, each with the big count over a
                                    dotted status label. */}
                                <div className="tpcrm-cov-keys">
                                    <button
                                        type="button"
                                        className="tpcrm-cov-stat tpcrm-cov-stat--done"
                                        onClick={() => openDrill('Assessed vendors', 'Have been sent a questionnaire',
                                            safeArray(vendors).filter((v) => v?.latest_assessment?.questionnaire_display))}
                                    >
                                        <span className="tpcrm-cov-stat-num">{coverage.assessed}</span>
                                        <span className="tpcrm-cov-stat-label tpcrm-cov-stat-label--done">assessed</span>
                                    </button>
                                    <button
                                        type="button"
                                        className="tpcrm-cov-stat tpcrm-cov-stat--gap"
                                        onClick={() => openDrill('Never assessed', 'No questionnaire has been sent',
                                            safeArray(vendors).filter((v) => !v?.latest_assessment?.questionnaire_display))}
                                    >
                                        <span className="tpcrm-cov-stat-num">{coverage.never}</span>
                                        <span className="tpcrm-cov-stat-label tpcrm-cov-stat-label--gap">never assessed</span>
                                    </button>
                                </div>

                                {coverage.never > 0 && (
                                    <div className="tpcrm-cov-gapbox">
                                        <div className="tpcrm-mini-title">Not yet assessed</div>
                                        <div className="tpcrm-chips">
                                            {coverage.neverNames.map((name) => (
                                                <span className="tpcrm-chip" key={name} title={name}>{name}</span>
                                            ))}
                                            {coverage.never > coverage.neverNames.length && (
                                                <button
                                                    type="button"
                                                    className="tpcrm-chip tpcrm-chip--more tpcrm-drill"
                                                    title={`View all ${coverage.never} vendors that have never been assessed`}
                                                    onClick={() => openDrill('Never assessed', 'No questionnaire has been sent',
                                                        safeArray(vendors).filter((v) => !v?.latest_assessment?.questionnaire_display))}
                                                >
                                                    {`+${coverage.never - coverage.neverNames.length} more`}
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* Data exposure. New information, not a restatement: these
                    seven booleans exist on every vendor and feed the
                    criticality score, but were only ever visible one vendor at
                    a time inside a form. */}
                <div className="tpcrm-card cmn_elevated_card">
                    <div className="tpcrm-card-header">
                        <div>
                            <div className="d-flex align-items-center gap-2">
                                <ModuleIcon name="risk" size={22} color="#1e3c65" />
                                <h3 className="mb-0">Sensitive Data Exposure</h3>
                            </div>
                            <div className="tpcrm-card-subtitle">How many vendors can reach each class of data</div>
                        </div>
                    </div>

                    {exposure.total === 0 ? (
                        <p className="tpcrm-empty-note">No vendors yet.</p>
                    ) : (
                        <div className="tpcrm-exp-mini">
                            {exposure.rows.map((row) => {
                                const tone = criticalityTone(row.tier);
                                const pct = exposure.total ? Math.round((row.count / exposure.total) * 100) : 0;
                                return (
                                    <AddOnCard
                                        key={row.key}
                                        title={`${row.count} of ${exposure.total} vendors can reach ${row.label}`}
                                        onClick={() => openDrill(
                                            row.label,
                                            `${row.tier} sensitivity`,
                                            safeArray(vendors).filter((v) => v?.[row.key]),
                                        )}
                                        style={{ height: '100%', padding: '10px 12px', gap: 10 }}
                                        label={(
                                            <span style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                                                <Body as="span" variant={2} weight="semibold" color="var(--color-text-primary)">{row.label}</Body>
                                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, minWidth: 0 }}>
                                                    <span style={{ flex: '0 0 auto', width: 7, height: 7, borderRadius: '50%', background: tone.color }} />
                                                    <Body as="span" variant={2} color="var(--color-text-muted)">
                                                        {row.tier} · {pct}%
                                                    </Body>
                                                </span>
                                            </span>
                                        )}
                                        action={(
                                            <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 2 }}>
                                                <Heading as="span" variant={3} color="var(--color-text-primary)">{row.count}</Heading>
                                                <Body as="span" variant={2} color="var(--color-text-muted)">/ {exposure.total}</Body>
                                            </span>
                                        )}
                                    />
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>

            {/* Reassessment workload. Bucketed by calendar month, which is what
                people plan against — and a column chart shows a spike coming
                three months out, which a list of the next five due dates
                cannot. */}
            {/* <div className="tpcrm-analytics-row tpcrm-analytics-row--single">
                <div className="tpcrm-card">
                    <div className="tpcrm-card-header">
                        <div>
                            <h3>Reassessment Workload</h3>
                            <div className="tpcrm-card-subtitle">Vendors falling due each month, from your Scan Cadence settings</div>
                        </div>
                        {workload.unscheduled.length > 0 && (
                            <button
                                type="button"
                                className="tpcrm-wl-note tpcrm-drill"
                                onClick={() => openDrill('Not scheduled', 'No next scan date set', workload.unscheduled)}
                            >
                                {`${workload.unscheduled.length} vendor${workload.unscheduled.length === 1 ? '' : 's'} not scheduled`}
                            </button>
                        )}
                    </div>

                    <div className="tpcrm-wl">
                        <div className={`tpcrm-wl-col${workload.overdue.length > 0 ? ' tpcrm-wl-col--over' : ''}`}>
                            <button
                                type="button"
                                className="tpcrm-wl-value tpcrm-drill"
                                onClick={() => openDrill('Overdue reassessments', 'Next scan date already passed', workload.overdue)}
                            >
                                {workload.overdue.length}
                            </button>
                            <span className="tpcrm-wl-track">
                                <span className="tpcrm-wl-bar" style={{ height: barHeight(workload.overdue.length) }} />
                            </span>
                            <span className="tpcrm-wl-label">Overdue</span>
                        </div>

                        <span className="tpcrm-wl-divider" aria-hidden="true" />

                        {workload.months.map((month) => (
                            <div className="tpcrm-wl-col" key={month.key}>
                                <button
                                    type="button"
                                    className="tpcrm-wl-value tpcrm-drill"
                                    onClick={() => openDrill(`Due in ${month.longLabel}`, 'From your Scan Cadence settings', month.vendors)}
                                >
                                    {month.count}
                                </button>
                                <span className="tpcrm-wl-track">
                                    <span className="tpcrm-wl-bar" style={{ height: barHeight(month.count) }} />
                                </span>
                                <span className="tpcrm-wl-label">
                                    {month.label}
                                    {month.yearMark && <em className="tpcrm-wl-year">{month.yearMark}</em>}
                                </span>
                            </div>
                        ))}

                        <span className="tpcrm-wl-divider" aria-hidden="true" />

                        <div className="tpcrm-wl-col tpcrm-wl-col--later">
                            <button
                                type="button"
                                className="tpcrm-wl-value tpcrm-drill"
                                onClick={() => openDrill('Due beyond 12 months', 'Past the end of this axis', workload.beyond)}
                            >
                                {workload.beyond.length}
                            </button>
                            <span className="tpcrm-wl-track">
                                <span className="tpcrm-wl-bar" style={{ height: barHeight(workload.beyond.length) }} />
                            </span>
                            <span className="tpcrm-wl-label">Later</span>
                        </div>
                    </div>
                </div>
            </div> */}

            {/* No analysisDate here on purpose: both KPI cards that open this
                modal are a fixed "last 30 days" stat (see their hint text),
                not "within the page's selected range" — the incidents/
                critical-impact endpoints only accept a single exact-day
                analysis_date (no start/end window), so passing this page's
                adjustable date filter through pinned the modal to one exact
                day, which almost never has a hit even when the 30-day count
                on the card is nonzero. */}
            <VendorIssuesModal
                show={Boolean(issuesModal)}
                variant={issuesModal}
                onHide={() => setIssuesModal(null)}
            />

            <VendorListModal
                show={Boolean(drill)}
                title={drill?.title}
                subtitle={drill?.subtitle}
                vendors={drill?.vendors}
                onHide={() => setDrill(null)}
            />

            <AverageGradeModal
                show={showAvgGradeModal}
                onHide={() => setShowAvgGradeModal(false)}
                counts={gradeBuckets}
            />
        </div>
    );
}

export default TpcrmAnalyticsView;
